import { lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import DashboardLayout from '@layout/DashboardLayout/DashboardLayout';
import PublicLayout from '@layout/PublicLayout/PublicLayout';
import ParkerLayout from '@layout/ParkerLayout/ParkerLayout';
import GroupAdminLayout from '@layout/GroupAdminLayout/GroupAdminLayout';
import GroupAdminDashboardLayout from '@layout/GroupAdminLayout/DashboardLayout/DashboardLayout';
import OperatorAuthLayout from '@layout/OperatorLayout/Auth/OperatorAuthLayout';
import PaymentState from '../context/provider';
import UserLayout from '../layout/UserLayout/UserLayout';
import SessionDetails from '../components/SessionDetails/SessionDetails';
import NearbyLocation from '../components/NearbyLocation/NearbyLocation';
import SessionDetailMobile from '../components/SessionDetailMobile/SessionDetailMobile';
import SessionViewDetails from '../components/Modals/SessionViewDetails/SessionViewDetails';
import ManageSubscription from '../components/Modals/ManageSubscription/ManageSubscription';
import ManageSubscriptionUpdate from '../components/Modals/ManageSubscription/ManageSubscriptionUpdate';
import AddVehicle from '../components/Modals/AddVehicle/AddVehicle';
import ViewVehicle from '../components/Modals/ViewVehicle/ViewVehicle';
import Parkers from '../pages/Parkers/Parkers';
import ViewParkers from '../components/ViewParkers/ViewParkers';
import EditParker from '../components/EditParker/EditParker';
import AddGroup from '../pages/Operator/Groups/AddGroup/AddGroup';
import EditGroup from '../pages/Operator/Groups/EditGroup/EditGroup';
import OperatorLayout from '../layout/OperatorLayout/OperatorLayout';
import GroupDetails from '../pages/Operator/Groups/GroupDetails/GroupDetails';
import ViewGroupDetails from '../pages/Operator/Groups/GroupDetails/ViewGroupDetails';
import GroupSubscriptionsDetail from '../components/GroupedSubscriptions/GroupSubscriptionsDetail';

// Auth routes
const Login = lazy(() => import('@pages/Auth/Login'));
const ForgotPassword = lazy(() => import('@pages/Auth/ForgotPassword/ForgotPassword'));
const OtpScreen = lazy(() => import('@pages/Auth/ForgotPassword/OtpScreen'));
const ResetPassword = lazy(() => import('@pages/Auth/ForgotPassword/ResetPassword'));

// Dashboard routes
const Home = lazy(() => import('@pages/Home/Home'));
const AddLocation = lazy(() => import('@pages/AddLocation/AddLocation'));
const EditLocation = lazy(() => import('@pages/EditLocation/EditLocation'));
const Transactions = lazy(() => import('@pages/Transactions/Transactions'));
const Reports = lazy(() => import('@pages/Reports/Reports'));
const LPR = lazy(() => import('@pages/LPR/LPR'));
const UserProfile = lazy(() => import('@pages/UserProfile/UserProfile'));
const Profile = lazy(() => import('@pages/UserProfile/Profile'));
const NotFound = lazy(() => import('@pages/NotFound/NotFound'));
const ParkerSubscriptionsForAdmin = lazy(() => import('@pages/Admin/Subscriptions/ParkerSubscriptions'));
const ParkerPaymentsForAdmin = lazy(() => import('@pages/Admin/ParkerPayments/ParkerPayments'));

// Parking routes
const Zone = lazy(() => import('@pages/Parking/Zone/Zone'));
const Duration = lazy(() => import('@pages/Parking/Duration/Duration'));
const Vehicle = lazy(() => import('@pages/Parking/Vehicle/Vehicle'));
const Review = lazy(() => import('@pages/Parking/Review/Review'));
const Success = lazy(() => import('@pages/Parking/Success/Success'));
const MultifamilyGuestLoginPage = lazy(
    () => import('@pages/Parking/MultifamilyGuestLoginPage/MultifamilyGuestLoginPage')
);

// Parker routes
const ParkerLogin = lazy(() => import('@pages/Parker/Auth/Login'));
const ParkerSignup = lazy(() => import('@pages/Parker/Auth/Signup'));
const ParkerSignupConfirmation = lazy(() => import('@pages/Parker/Auth/SignupConfirmation'));
const ParkerSignupInformationVehicle = lazy(() => import('@pages/Parker/Auth/SignupInformationVehicle'));
const ParkerSignupInformation = lazy(() => import('@pages/Parker/Auth/SignupInformation'));
const ParkerSignupPasswordSetup = lazy(() => import('@pages/Parker/Auth/SignupPasswordSetup'));
const ParkerForgotPassword = lazy(() => import('@pages/Parker/Auth/ForgotPassword'));
const ParkerOtpScreen = lazy(() => import('@pages/Parker/Auth/OtpScreen'));
const ParkerResetPassword = lazy(() => import('@pages/Parker/Auth/ResetPassword'));

// Parker module imports
const ParkerHome = lazy(() => import('@pages/Parker/Home/Home'));
const ParkerLocations = lazy(() => import('@pages/Parker/Locations/Locations'));
const ParkerSubscriptions = lazy(() => import('@pages/Parker/Subscriptions/Subscriptions'));
const ParkerPayments = lazy(() => import('@pages/Parker/Payments/Payments'));
const ParkerVehicles = lazy(() => import('@pages/Parker/Vehicles/Vehicles'));
const ParkerSupport = lazy(() => import('@pages/Parker/Support/Support'));
const ParkerAccountSettings = lazy(() => import('@pages/Parker/AccountSettings/AccountSettings'));
const Validations = lazy(() => import('@pages/Validations/Validations'));
const ValidationsList = lazy(() => import('@pages/Validations/ValidationsList'));
const PublicPortal = lazy(() => import('@pages/Validations/PublicPortal'));

// Operator Protected Routes
const OperatorGroups = lazy(() => import('@pages/Operator/Groups/Groups'));
const EventRates = lazy(() => import('@pages/Events/EventRates/EventRates'));
const Promotions = lazy(() => import('@pages/Events/Promotions/Promotions'));
const OperatorAccountSettings = lazy(() => import('@pages/Operator/AccountSettings/AccountSettings'));

// Operator Public Routes
const OperatorLogin = lazy(() => import('@pages/Operator/Auth/Login'));

// NEW: Lazy-load Group Admin pages
const GroupAdminDashboard = lazy(() => import('@pages/GroupAdmin/Dashboard/Dashboard'));
const GroupAdminSubscriptions = lazy(() => import('@pages/GroupAdmin/Subscriptions/Subscriptions'));
const GroupAdminSubscriptionsDetail = lazy(() => import('@pages/GroupAdmin/Subscriptions/SubscriptionsDetail'));
const GroupAdminValidate = lazy(() => import('@pages/GroupAdmin/Validate/Validate'));
const GroupAdminBilling = lazy(() => import('@pages/GroupAdmin/Billing/Billing'));
const GroupAdminActivity = lazy(() => import('@pages/GroupAdmin/Activity/Activity'));
const GroupAdminAccountSettings = lazy(() => import('@pages/GroupAdmin/AccountSettings/AccountSettings'));

// For Group Admin Auth
const GroupAdminWelcome = lazy(() => import('@pages/GroupAdmin/Auth/Welcome'));
const GroupAdminResetPassword = lazy(() => import('@pages/GroupAdmin/Auth/ResetPassword'));
const OperatorResetPassword = lazy(() => import('@pages/Operator/Auth/ResetPassword'));

const GroupAdminLogin = lazy(() => import('@pages/GroupAdmin/Auth/Login'));

const MainRoutes = () => {
    return (
        <PaymentState>
            <Routes>
                {/* Admin public routes */}
                <Route path="/admin" element={<PublicLayout />}>
                    <Route path="login" exact element={<Login />} />
                    <Route path="forgot-password" exact element={<ForgotPassword />} />
                    <Route path="forgot-password/verify-otp" exact element={<OtpScreen />} />
                    <Route path="forgot-password/reset-password" exact element={<ResetPassword />} />
                </Route>

                {/* Parker public routes */}
                <Route path="/parker/auth" element={<ParkerLayout />}>
                    <Route index exact element={<ParkerLogin />} />
                    <Route path="signup" exact element={<ParkerSignup />} />
                    <Route path="signup-confirmation" exact element={<ParkerSignupConfirmation />} />
                    <Route path="signup-information-vehicle" exact element={<ParkerSignupInformationVehicle />} />
                    <Route path="signup-information" exact element={<ParkerSignupInformation />} />
                    <Route path="signup-password" exact element={<ParkerSignupPasswordSetup />} />
                    <Route path="forgot-password" exact element={<ParkerForgotPassword />} />
                    <Route path="forgot-password/verify-otp" exact element={<ParkerOtpScreen />} />
                    <Route path="forgot-password/reset-password" exact element={<ParkerResetPassword />} />
                </Route>

                {/* Operator public routes */}
                <Route path="/operator/auth" element={<OperatorLayout />}>
                    <Route index exact element={<OperatorLogin />} />
                    <Route path="forgot-password" exact element={<ForgotPassword />} />
                    <Route path="forgot-password/verify-otp" exact element={<OtpScreen />} />
                    <Route path="forgot-password/reset-password" exact element={<ResetPassword />} />
                    <Route path="reset-password/" exact element={<OperatorResetPassword />} />
                </Route>

                {/* Group Admin public routes */}
                <Route path="/group-admin/auth" element={<GroupAdminLayout />}>
                    <Route index exact element={<GroupAdminLogin />} />
                    <Route path="welcome" exact element={<GroupAdminWelcome />} />
                    <Route path="reset-password" exact element={<GroupAdminResetPassword />} />
                    <Route path="forgot-password" exact element={<ForgotPassword />} />
                    <Route path="forgot-password/verify-otp" exact element={<OtpScreen />} />
                    <Route path="forgot-password/reset-password" exact element={<ResetPassword />} />
                </Route>

                {/* Group Admin protected routes */}
                <Route path="/group-admin" element={<GroupAdminDashboardLayout />}>
                    <Route index exact element={<GroupAdminDashboard />} />
                    <Route path="subscriptions" exact element={<GroupAdminSubscriptions />} />

                    <Route
                        path="subscriptions-detail/:subscriptionId"
                        exact
                        element={<GroupAdminSubscriptionsDetail />}
                    />
                    <Route path="validate" exact element={<ValidationsList />} />
                    <Route path="validation-details/:validationId" exact element={<Validations />} />
                    <Route path="billing" exact element={<GroupAdminBilling />} />
                    <Route path="activity" exact element={<GroupAdminActivity />} />
                    <Route path="account-settings" exact element={<Profile />} />
                </Route>

                {/* Parker protected routes */}
                <Route path="/parker" element={<UserLayout />}>
                    <Route index exact element={<ParkerHome />} />
                    <Route path="session-details" exact element={<SessionDetails />} />
                    <Route path="session-details-mobile" exact element={<SessionDetailMobile />} />
                    <Route path="session-view-details" exact element={<SessionViewDetails />} />
                    <Route path="manage-subscription" exact element={<ManageSubscription />} />
                    <Route path="manage-update-subscription" exact element={<ManageSubscriptionUpdate />} />
                    <Route path="manage-group-detail-subscription" exact element={<GroupSubscriptionsDetail />} />
                    <Route path="locations" exact element={<ParkerLocations />} />
                    <Route path="nearby-location" exact element={<NearbyLocation />} />
                    <Route path="subscriptions" exact element={<ParkerSubscriptions />} />
                    <Route path="payments" exact element={<ParkerPayments />} />
                    <Route path="vehicles" exact element={<ParkerVehicles />} />
                    <Route path="add-vehicle" exact element={<AddVehicle />} />
                    <Route path="edit-vehicle" exact element={<AddVehicle />} />
                    <Route path="view-vehicle" exact element={<ViewVehicle />} />
                    <Route path="support" exact element={<ParkerSupport />} />
                    <Route path="account-settings" exact element={<ParkerAccountSettings />} />
                </Route>

                <Route path="/">
                    <Route index exact element={<Zone />} />
                    <Route path="zone" exact element={<Zone />} />
                    <Route path="zone/:zoneID" exact element={<Zone />} />
                    <Route path="duration" exact element={<Duration />} />
                    <Route path="vehicle" exact element={<Vehicle />} />
                    <Route path="review" exact element={<Review />} />
                    <Route path="success" exact element={<Success />} />
                    <Route path="validator-portal/:validationId" exact element={<PublicPortal />} />
                    <Route path="multifamily/:locationId" exact element={<MultifamilyGuestLoginPage />} />
                </Route>

                {/* Admin protected routes */}
                <Route path="/admin" element={<DashboardLayout />}>
                    <Route index exact element={<Home />} />
                    <Route path="add-location" exact element={<AddLocation />} />
                    <Route path="edit-location/:locationId" exact element={<EditLocation />} />
                    <Route path="transactions" exact element={<Transactions />} />
                    <Route path="parkers/:id" exact element={<ViewParkers />} />
                    <Route path="edit-parker/:id" exact element={<EditParker />} />
                    <Route path="parkers" exact element={<Parkers />} />
                    <Route path="subscriptions" exact element={<ParkerSubscriptionsForAdmin />} />
                    <Route path="parker-payments" exact element={<ParkerPaymentsForAdmin />} />
                    <Route path="reports" exact element={<Reports />} />
                    <Route path="lpr" exact element={<LPR />} />
                    <Route path="profile" exact element={<Profile />} />
                    {/* Operator routes */}
                    <Route path="groups" exact element={<OperatorGroups />} />
                    <Route path="add-group" exact element={<AddGroup />} />
                    <Route path="edit-group/:groupId" exact element={<EditGroup />} />
                    <Route path="group-details/:groupId" exact element={<GroupDetails />} />
                    <Route path="view-group-details/:groupId" exact element={<ViewGroupDetails />} />
                    <Route path="event-rates" exact element={<EventRates />} />
                    <Route path="promotions" exact element={<Promotions />} />
                    <Route path="account-settings" exact element={<Profile />} />
                    <Route path="promotions" exact element={<Promotions />} />
                    {/* <Route path="validations" exact element={<Validations />} /> */}
                </Route>
                {/* 404 Not Found */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </PaymentState>
    );
};

export default MainRoutes;
